import { useQuery } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { UserSearchResultResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The doubles partner picker's user search (registration S3). A LOCAL tournaments
 * hook that calls the SHARED social client (GET /social/users/search?q= via
 * socialApi.searchUsers) — the social module's useUserSearch can't be imported
 * across the sibling-module boundary, so the panel uses this in its place. Mirrors
 * useUserSearch's shape exactly: returns the server's PUBLIC-SAFE results VERBATIM
 * ({user_id, display_name, avatar_url}) — never a bare uid, never a client-derived
 * relationship state.
 *
 * The caller passes the DEBOUNCED, TRIMMED query. The query only RUNS when that
 * value is ≥ 2 chars (`enabled`) — the server requires q (min_length=2) and the
 * client guards a blank query (no fetch). A shorter / blank query keeps the query
 * idle (no fetch, no stale results). Keyed by the trimmed query so each distinct
 * search gets its OWN cache slot. No client sort, no derived state — self-exclusion
 * is the panel's responsibility (it owns the auth uid).
 */
export function usePartnerSearch(query: string) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;
  return useQuery<UserSearchResultResponse[]>({
    queryKey: queryKeys.tournaments.partnerSearch(trimmed),
    queryFn: () => socialApi.searchUsers(trimmed),
    enabled,
  });
}
